
/**
 * Image handling utility
 * Provides comprehensive image handling, optimization, and fallback logic
 */

/**
 * Handles image load errors by setting a fallback image
 * @param event - The error event from the image
 * @param fallbackSrc - Optional custom fallback image source
 */
export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement>, 
  fallbackSrc: string = '/placeholder.svg'
): void => {
  const target = event.target as HTMLImageElement;
  
  // Try to use WebP version first if original failed
  if (!target.src.includes('placeholder') && !target.src.includes('fallback') && !target.src.endsWith('.webp')) {
    const webpSrc = target.src.replace(/\.(jpg|jpeg|png)$/i, '.webp');
    console.log(`Attempting to load WebP version: ${webpSrc}`);
    target.src = webpSrc;
    return;
  }
  
  console.log(`Using fallback image: ${fallbackSrc}`);
  target.src = fallbackSrc;
  target.onerror = null; // Prevents infinite error loop
  
  // Add error class for optional styling
  target.classList.add('image-error');
};

/**
 * Creates an image onError handler with a specified fallback image
 * @param fallbackSrc - The fallback image source to use
 * @returns A function that can be used as an onError handler
 */
export const createImageErrorHandler = (fallbackSrc: string = '/placeholder.svg') => {
  return (event: React.SyntheticEvent<HTMLImageElement>): void => {
    handleImageError(event, fallbackSrc);
  };
};

/**
 * Optimizes an image URL by adding width and quality parameters if it's a known CDN
 * @param url - Original image URL
 * @param width - Desired width
 * @param quality - Desired quality (1-100)
 * @returns Optimized image URL
 */
export const optimizeImageUrl = (url: string, width?: number, quality: number = 80): string => {
  if (!url) return '/placeholder.svg';
  
  // Return as-is if it's a relative URL
  if (url.startsWith('/')) return url;
  
  // Handle Unsplash images
  if (url.includes('unsplash.com')) {
    return width ? `${url}&w=${width}&q=${quality}&auto=format` : url;
  }
  
  // Handle Cloudinary images
  if (url.includes('cloudinary.com')) {
    return width ? url.replace('/upload/', `/upload/w_${width},q_${quality}/`) : url;
  }
  
  // Return original URL for unknown sources
  return url;
};

/**
 * Generates srcset for responsive images
 * @param baseUrl - Base image URL
 * @param widths - Array of widths for srcset
 * @param quality - Image quality (1-100)
 * @returns srcset string
 */
export const generateSrcSet = (baseUrl: string, widths: number[] = [640, 960, 1280, 1920], quality: number = 80): string => {
  if (!baseUrl || baseUrl.startsWith('data:') || baseUrl.includes('placeholder')) return '';
  
  return widths
    .map(width => `${optimizeImageUrl(baseUrl, width, quality)} ${width}w`)
    .join(', ');
};
